/**
 * updateCell.ts - Pure mutation function for updating a single cell
 */

import produce from 'immer';

import type {Cell} from 'notebook/tables/constants/CellConstants';
import {EVALUABLE_CELL_PROPERTIES} from 'notebook/tables/constants/CellConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableMutationResult, type TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {sanitizeTypedValueForSave} from 'notebook/tables/typed-values/DataTypes';
import {omit} from 'util/omitKey';

export interface UpdateCellOptions {
  /** If true, add a new blank row at the bottom of the table */
  expandTable?: boolean;
  /** If present and expandTable is true, remerge single input columns */
  singleInputBoundaryIndex?: number | null;
}

/**
 * Update a single cell in the table.
 *
 * @param table - The current table
 * @param index - The [row, column] index of the cell to update
 * @param cellData - Partial cell data to merge into the existing cell
 * @param options - Optional configuration for table expansion
 * @returns The mutation result with new table, operation, and affected cells
 */
export function updateCell(
  table: Table,
  index: TableIndex,
  cellData: Partial<Cell>,
  options: UpdateCellOptions = {}
): TableMutationResult {
  const [i, j] = index;
  const oldCell = table.table[i]?.[j];

  // Strip merge properties - these should only be modified through mergeRegion/unmergeRegion TableOps
  cellData = omit(cellData, 'mergeRowSpan', 'mergeColSpan');

  // Check if any properties that trigger evaluation have changed
  let requiresEvaluation = false;
  for (const property of Object.keys(cellData)) {
    const propertyKey = property as keyof Cell;
    if (
      [...EVALUABLE_CELL_PROPERTIES].includes(propertyKey) &&
      cellData[propertyKey] !== oldCell[propertyKey]
    ) {
      requiresEvaluation = true;
      break;
    }
  }

  // Perform the cell update inline (replaces updateCellInTable)
  // Any usage of cell.error should be computed and automatically reapplied by the store if still applicable
  const baseCell = omit(oldCell, 'error', 'typedValue');
  const newCell: Cell = {...baseCell, ...cellData};
  if (cellData.typedValue != null) {
    newCell.typedValue = sanitizeTypedValueForSave(cellData.typedValue);
  }

  let updatedTable = produce(table, (draftTable) => {
    draftTable.table[i][j] = newCell;
  });
  let tableOp: TableOpDiff | null = requiresEvaluation
    ? {type: 'update-cells', cells: [{row: i, column: j}]}
    : null;

  // If 'expand' is true, add a new blank row at the bottom of the table
  if (options.expandTable) {
    const newRowIndex = updatedTable.table.length;
    updatedTable = produce(updatedTable, (draft) => {
      draft.table = [...draft.table, ...TableUtil.makeBlankRows(draft, 1)];
    });
    requiresEvaluation = true;
    // override the tableOp to use upsert-cells since we're adding a new row
    tableOp = {
      type: 'upsert-cells',
      cells: [{row: i, column: j}],
      newRowIndexes: [newRowIndex],
    };

    // If any single inputs are present and the table is expanding, merge the single input columns
    if (options.singleInputBoundaryIndex) {
      updatedTable = produce(updatedTable, (draft) => {
        draft.table = TableUtil.remergeSingleInputColumns(draft).table;
      });
    }
  }

  return {
    table: updatedTable,
    tableOp,
    affectedCells: [{row: i, column: j, oldCell, newCell}],
  };
}
